import { getTapeVariant, getTapeRotation } from '@/lib/polaroid-utils';
import { TAPE_VARIANT_COUNT } from '@/lib/dittoTheme';
import { cn } from '@/lib/utils';

interface WashiTapeProps {
  eventId: string;
  className?: string;
  /** Size variant: 'sm' for grid cards, 'lg' for feed polaroids */
  size?: 'sm' | 'lg';
  /** Variant number to avoid (prevents consecutive same-color tapes) */
  avoidVariant?: number;
}

export function WashiTape({ eventId, className, size = 'sm', avoidVariant }: WashiTapeProps) {
  let variant = getTapeVariant(eventId);
  // If this variant matches the one to avoid, bump to the next one
  if (avoidVariant !== undefined && variant === avoidVariant) {
    variant = (variant + 1) % TAPE_VARIANT_COUNT;
  }
  const rotation = getTapeRotation(eventId);
  const isLg = size === 'lg';

  // 8 tape variants — each with a unique visual pattern.
  // Colors are driven by --tape-* CSS variables which adapt to the Ditto theme.
  const variantClasses: Record<number, string> = {
    0: 'overflow-hidden',     // glitter sparkle — bg via CSS variable
    1: 'tape-stripes-mint',   // diagonal stripes (text hue-shifted)
    2: 'tape-rainbow',        // rainbow diagonal stripes (all colors)
    3: 'tape-polkadot',       // polka dot (primary hue-shifted +90°)
    4: 'tape-stripes-text',   // diagonal stripes (text color based)
    5: 'tape-checkerboard',   // checkerboard (primary hue-shifted -60°)
    6: 'tape-crosshatch',     // crosshatch grid (bg-image accent or text +150°)
    7: 'tape-zigzag',         // zigzag (bg-image accent or primary -120°)
  };

  // Variant 0 uses an inline style for its background color so it can
  // reference the CSS variable directly (Tailwind can't resolve var() at build time).
  const inlineStyle: React.CSSProperties = {
    transform: `translateX(-50%) translateY(${isLg ? '-14px' : '-9px'}) rotate(${rotation}deg)`,
    ...(variant === 0 ? { backgroundColor: 'var(--tape-0-base)' } : {}),
  };

  return (
    <div
      className={cn(
        'absolute top-0 left-1/2 z-10 rounded-[2px]',
        isLg
          ? 'w-[120px] h-[28px]'
          : 'w-[70px] h-[18px]',
        variantClasses[variant],
        className,
      )}
      style={inlineStyle}
    >
      {/* Sparkle dots for glitter variant */}
      {variant === 0 && (
        <>
          <span className="absolute w-1 h-1 rounded-full bg-white animate-tape-sparkle top-[3px] left-[12px]" />
          <span className="absolute w-[3px] h-[3px] rounded-full bg-white animate-tape-sparkle top-[8px] left-[32px]" style={{ animationDelay: '0.7s' }} />
          <span className="absolute w-1 h-1 rounded-full bg-white animate-tape-sparkle top-[5px] left-[52px]" style={{ animationDelay: '1.3s' }} />
          {isLg && (
            <>
              <span className="absolute w-1 h-1 rounded-full bg-white animate-tape-sparkle top-[14px] left-[72px]" style={{ animationDelay: '0.4s' }} />
              <span className="absolute w-[3px] h-[3px] rounded-full bg-white animate-tape-sparkle top-[8px] left-[96px]" style={{ animationDelay: '1.8s' }} />
            </>
          )}
        </>
      )}
    </div>
  );
}
